// Whether the changelist GitHub App can write to a repository for the
// signed-in user. A user's App token reaches only repositories the App is
// installed on: elsewhere (a public repository) it reads but every write
// fails with "Resource not accessible by integration". Checked once per
// repository per page load through the bridge:
//   GET user/installations                      (the App's installations the user can see)
//   GET user/installations/{id}/repositories    (for an installation on selected repositories)
// A token that is not an App user token (the development token sign-in)
// gets 403 from user/installations; the check is then skipped ('unknown').
// Both endpoints list only what the user has explicit permission on, so a
// missing installation proves nothing on a public repository the user has no
// role on (a drive-by reviewer): that answer is 'unknown', and a write that
// fails for the App's access marks the PR read-only instead.
import { HttpError } from './cache';
import { rest, restPages } from './gh';
import type { AccountRow, Owner, Reach } from '../logic/onboarding';

export interface Installation {
  id: number;
  /** `type` is `User`, `Organization` or `Enterprise`. */
  account: { id?: number; login: string; type?: string; avatar_url?: string | null } | null;
  repository_selection?: 'all' | 'selected';
  suspended_at?: string | null;
  permissions?: Record<string, string>;
}

export interface InstallationRepo {
  full_name?: string;
  name: string;
  owner?: { login: string } | null;
}

/**
 * `covered`: the App is installed on the repository with Pull requests:
 * write. `not_installed`: no (active) installation covers it. `no_permission`:
 * an installation covers it without Pull requests: write. `unknown`: the
 * check could not run.
 */
export type Coverage = 'covered' | 'not_installed' | 'no_permission' | 'unknown';

const same = (a: string | null | undefined, b: string) => !!a && a.toLowerCase() === b.toLowerCase();

/** The active installation on `owner`, if any (suspended ones grant nothing). */
export function installationFor(installations: Installation[], owner: string): Installation | null {
  return installations.find((i) => same(i.account?.login, owner) && !i.suspended_at) ?? null;
}

/** True when `repos` (an installation's selected repositories) include `owner/repo`. */
export function listsRepo(repos: InstallationRepo[], owner: string, repo: string): boolean {
  return repos.some((r) =>
    r.full_name ? same(r.full_name, `${owner}/${repo}`) : same(r.name, repo) && (!r.owner || same(r.owner.login, owner)),
  );
}

/** Missing permissions means GitHub did not say: assume the App's manifest (Pull requests: write). */
function canWritePulls(i: Installation): boolean {
  const p = i.permissions?.pull_requests;
  return p === undefined || p === 'write';
}

/**
 * Coverage from the installations list and, for an installation on
 * selected repositories, a lookup of its repositories.
 */
export async function coverageFrom(
  installations: Installation[],
  owner: string,
  repo: string,
  reposOf: (id: number) => Promise<InstallationRepo[]>,
): Promise<Coverage> {
  const inst = installationFor(installations, owner);
  if (!inst) return 'not_installed';
  if (inst.repository_selection !== 'all' && !listsRepo(await reposOf(inst.id), owner, repo)) return 'not_installed';
  return canWritePulls(inst) ? 'covered' : 'no_permission';
}

/**
 * Whether the viewer's access to a repository is explicit, so that GitHub's
 * installation lists would include an installation covering it: every
 * access to a private repository is, and on a public one a role above read
 * (`viewerPermission` TRIAGE, WRITE, MAINTAIN or ADMIN). READ on a public
 * repository may be the implicit access everyone has.
 */
export function explicitAccess(repo: { isPrivate?: boolean | null; viewerPermission: string | null }): boolean {
  return !!repo.isPrivate || ['TRIAGE', 'WRITE', 'MAINTAIN', 'ADMIN'].includes(repo.viewerPermission ?? '');
}

// ---------------------------------------------------------------- session cache

let installations: Promise<Installation[] | null> | null = null;
const repos = new Map<number, Promise<InstallationRepo[]>>();
const results = new Map<string, Promise<Coverage>>();

/**
 * The user's installations of the App, or null when the token is not an App
 * user token (403; kept for the page's life). Any other failure rejects and
 * is asked again by the next call.
 */
function loadInstallations(): Promise<Installation[] | null> {
  if (!installations) {
    const p: Promise<Installation[] | null> = restPages<{ installations?: Installation[] }, Installation>(
      'user/installations?per_page=100',
      (b) => b.installations ?? [],
    ).catch((e) => {
      if (e instanceof HttpError && e.status === 403) return null;
      if (installations === p) installations = null;
      throw e;
    });
    installations = p;
  }
  return installations;
}

function loadRepos(id: number): Promise<InstallationRepo[]> {
  let p = repos.get(id);
  if (!p) {
    p = restPages<{ repositories?: InstallationRepo[] }, InstallationRepo>(
      `user/installations/${id}/repositories?per_page=100`,
      (b) => b.repositories ?? [],
    );
    repos.set(id, p);
    p.catch(() => repos.delete(id));
  }
  return p;
}

/**
 * The App's coverage of `owner/repo` for this user, cached for the page's
 * life. Without `explicitAccess` (see `explicitAccess`) a missing
 * installation answers 'unknown' rather than 'not_installed'.
 */
export async function repoCoverage(
  owner: string,
  repo: string,
  opts: { explicitAccess?: boolean } = {},
): Promise<Coverage> {
  const c = await listedCoverage(owner, repo);
  return c === 'not_installed' && !opts.explicitAccess ? 'unknown' : c;
}

/**
 * The repositories among `repos` that the App is known not to cover, as
 * lowercased `owner/name`: no active installation covers them, and the
 * viewer's access is explicit, so GitHub would have listed one
 * (`explicitAccess`). A repository whose privacy and permission were not
 * read, one covered without Pull requests: write, and any whose check could
 * not run are left out: nothing is claimed that is not known. Answered from
 * the page's cached listings (`repoCoverage`), so it asks GitHub nothing
 * once the installations are read.
 */
export async function uncoveredRepos(
  repos: readonly { nameWithOwner: string; isPrivate?: boolean | null; viewerPermission?: string | null }[],
): Promise<Set<string>> {
  const once = new Map(repos.map((r) => [r.nameWithOwner.toLowerCase(), r]));
  const out = new Set<string>();
  await Promise.all(
    [...once].map(async ([key, r]) => {
      const [owner, name] = r.nameWithOwner.split('/');
      const explicit = explicitAccess({ isPrivate: r.isPrivate, viewerPermission: r.viewerPermission ?? null });
      if (owner && name && (await repoCoverage(owner, name, { explicitAccess: explicit })) === 'not_installed') out.add(key);
    }),
  );
  return out;
}

/** How often, at most, coverage is asked again on returning to the page or the stream coming back. */
export const COVERAGE_RECHECK_MS = 60_000;

/** Something that may mean the App's installations changed under an open page. */
export type CoverageSignal = { kind: 'revoked'; reason: 'no_access' | 'not_installed' } | { kind: 'focus' | 'reconnect' | 'resync' | 'open' };

/**
 * Whether to ask GitHub again which repositories the App covers
 * (`resetCoverage`, then the listings), given when that was last asked.
 * - The event stream revoking a topic as `not_installed` says an
 *   installation went away: at once.
 * - The server sends no notice when the App is installed somewhere new, so
 *   an install is caught when the user returns to the page (they install
 *   on GitHub, in another tab) or the stream reconnects or resyncs, at
 *   most once per `COVERAGE_RECHECK_MS`.
 */
export function coverageMayHaveChanged(signal: CoverageSignal, lastAt: number, now: number): boolean {
  if (signal.kind === 'revoked') return signal.reason === 'not_installed';
  if (signal.kind === 'open') return false;
  return now - lastAt >= COVERAGE_RECHECK_MS;
}

function listedCoverage(owner: string, repo: string): Promise<Coverage> {
  const key = `${owner}/${repo}`.toLowerCase();
  let p = results.get(key);
  if (!p) {
    p = (async () => {
      try {
        const list = await loadInstallations();
        if (!list) return 'unknown' as Coverage;
        return await coverageFrom(list, owner, repo, loadRepos);
      } catch {
        return 'unknown' as Coverage;
      }
    })();
    results.set(key, p);
    // An unknown answer (a transient failure) is asked again next time.
    void p.then((c) => {
      if (c === 'unknown' && results.get(key) === p) results.delete(key);
    });
  }
  return p;
}

/**
 * The accounts the App is installed on that the viewer can see, each with
 * what its installation reaches (for one on selected repositories, those
 * the viewer can see: GitHub lists only those) and whose account it is to
 * the viewer (`Owner`; for an organization, from the viewer's membership).
 * Enterprise installations own no repositories and are left out. An
 * account that could not be looked into is a `failed` row, so one failure
 * leaves the others listed. Null when the sign-in cannot list installations
 * (not an App user token); a failed installations listing rejects. Shares
 * the page's cache with `repoCoverage`.
 */
export async function installedAccounts(viewer: { id: number; login: string }): Promise<AccountRow[] | null> {
  const list = await loadInstallations();
  if (!list) return null;
  const rows = list.flatMap((i) => {
    const a = i.account;
    if (!a || (a.type !== 'User' && a.type !== 'Organization')) return [];
    const type: 'User' | 'Organization' = a.type;
    return [{ i, type, own: a.id !== undefined ? a.id === viewer.id : same(a.login, viewer.login) }];
  });
  return Promise.all(
    rows.map(async ({ i, type, own }): Promise<AccountRow> => {
      const base = { installationId: i.id, login: i.account!.login, avatarUrl: i.account!.avatar_url ?? null };
      try {
        const [reach, owner] = await Promise.all([reachOf(i), ownerOf(i, type, own)]);
        return { status: 'listed', ...base, reach, owner };
      } catch (e) {
        return { status: 'failed', ...base, message: e instanceof Error ? e.message : String(e) };
      }
    }),
  );
}

/** Suspension first: a suspended installation reaches nothing, whatever its selection. */
async function reachOf(i: Installation): Promise<Reach> {
  if (i.suspended_at) return { kind: 'suspended' };
  if (i.repository_selection === 'all') return { kind: 'all' };
  return { kind: 'selected', count: (await loadRepos(i.id)).length };
}

async function ownerOf(i: Installation, type: 'User' | 'Organization', own: boolean): Promise<Owner> {
  if (own) return { kind: 'own' };
  if (type === 'User') return { kind: 'user', repo: await someRepo(i) };
  const role = await orgRole(i.account!.login);
  return role === 'outside' ? { kind: 'organization', role, repo: await someRepo(i) } : { kind: 'organization', role };
}

/** The viewer's role in an organization: an active admin or member, or outside it (no active membership). */
async function orgRole(org: string): Promise<'admin' | 'member' | 'outside'> {
  try {
    const m = await rest<{ state?: string; role?: string }>(`user/memberships/orgs/${encodeURIComponent(org)}`);
    if (m.state !== 'active') return 'outside';
    return m.role === 'admin' ? 'admin' : 'member';
  } catch (e) {
    if (e instanceof HttpError && (e.status === 404 || e.status === 403)) return 'outside';
    throw e;
  }
}

/** A repository of the installation's account the viewer can read, or null (a suspended installation lists none). */
async function someRepo(i: Installation): Promise<string | null> {
  if (i.suspended_at) return null;
  const owner = i.account!.login;
  const r = (await loadRepos(i.id)).find((r) => (r.full_name ? same(r.full_name.split('/')[0], owner) : !r.owner || same(r.owner.login, owner)));
  return r ? r.name : null;
}

/** Forget cached answers (after installing the App, and for tests). */
export function resetCoverage() {
  installations = null;
  repos.clear();
  results.clear();
}
